






















































































































import { AxiosError } from 'axios';
// utils helpers
import mixins from '@/utils/mixins';

// mixins
import componentState from '@/mixins/state';

import { ALForm } from '@/common/component.types';

import {
  IQuoteFreightTemplate,
  IQuoteFreightTemplateData,
  QuoteFreightTemplate,
  QuoteTemplateFieldError,
  QuoteTemplateTranslationLanguage
} from '@/models';
import { mapActions } from 'vuex';
import { ErrorManager } from '@/models/error';
import ErrorAlert from '../../../common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import QuillPDFEditor from '@/components/common/QuillPDFEditor.vue';
import QuoteTemplateNoteField from '../common/QuoteTemplateNoteField.vue';

const baseMixins = mixins(componentState);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuoteFreightFormDialog',

    components: {
      ErrorAlert,
      ALDialog,
      QuillPDFEditor,
      QuoteTemplateNoteField
    },

    data: () => ({
      valid: false,
      dialog: false,
      createForm: false,

      quoteFreightTemplate: new QuoteFreightTemplate(),
      quoteFreightTemplateTranslation: new QuoteFreightTemplate(),

      titleRules: [
        (v: any) => !!v || 'Title is required',
        (v: any) =>
          (v && v.length <= 128) || 'Title must be at most 128 characters'
      ],
      loading: false,

      errorMessage: '',
      detailErrorMessage: '',
      fieldError: new QuoteTemplateFieldError(),

      currentLanguage: QuoteTemplateTranslationLanguage.en,
      QuoteTemplateTranslationLanguage: QuoteTemplateTranslationLanguage,

      languagesList: [
        {
          text: 'English',
          value: QuoteTemplateTranslationLanguage.en
        },
        {
          text: 'Spanish',
          value: QuoteTemplateTranslationLanguage.es
        },
        {
          text: 'Russian',
          value: QuoteTemplateTranslationLanguage.ru
        }
      ]
    }),

    methods: {
      ...mapActions({
        updateQuoteFreightTemplate: 'quoteTemplate/updateQuoteFreightTemplate',
        createQuoteFreightTemplate: 'quoteTemplate/createQuoteFreightTemplate',
        fetchQuoteFreightTemplateTranslations:
          'quoteTemplate/fetchQuoteFreightTemplateTranslations',
        createQuoteFreightTemplateTranslation:
          'quoteTemplate/createQuoteFreightTemplateTranslation',
        updateQuoteFreightTemplateTranslation:
          'quoteTemplate/updateQuoteFreightTemplateTranslation'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
        this.fieldError.clearErrors();
      },

      open(quoteFreightTemplate: IQuoteFreightTemplate, createForm: boolean) {
        try {
          this.clearAllErrors();
          this.currentLanguage = QuoteTemplateTranslationLanguage.en;
          this.quoteFreightTemplate = new QuoteFreightTemplate(
            quoteFreightTemplate
          );
          this.quoteFreightTemplateTranslation = new QuoteFreightTemplate();
          this.createForm = createForm;
          this.dialog = true;
          this.setComponentState('loaded');

          this.resetFormValidation();
        } catch (err) {
          this.setComponentState('error', err);
        }
      },

      close() {
        this.dialog = false;
        this.$nextTick(() => {
          this.createForm = false;
        });
      },

      async getQuoteTemplateTranslation(
        language: QuoteTemplateTranslationLanguage
      ) {
        this.clearAllErrors();
        try {
          this.quoteFreightTemplateTranslation = new QuoteFreightTemplate();
          if (!this.quoteFreightTemplate.id) {
            return;
          }
          await this.fetchQuoteFreightTemplateTranslations(
            this.quoteFreightTemplate.id
          ).then((templates: IQuoteFreightTemplateData[]) => {
            const quoteFreightTemplate = templates.find(template => {
              return template.language === language;
            });
            if (quoteFreightTemplate) {
              this.quoteFreightTemplateTranslation = new QuoteFreightTemplate(
                quoteFreightTemplate
              );
            }
          });
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (
              QuoteTemplateFieldError.isQuoteTemplateFieldError(
                error.response.data
              )
            ) {
              this.fieldError = new QuoteTemplateFieldError(
                error.response.data
              );
            } else if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }

          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.loading = false;
        }
      },

      async save() {
        if (this.validateForm()) {
          this.clearAllErrors();
          this.loading = true;
          try {
            this.quoteFreightTemplateTranslation.language = this.currentLanguage;
            switch (this.currentLanguage) {
              case QuoteTemplateTranslationLanguage.es:
              case QuoteTemplateTranslationLanguage.ru:
                await this.fetchQuoteFreightTemplateTranslations(
                  this.quoteFreightTemplate.id
                ).then((templates: IQuoteFreightTemplateData[]) => {
                  const quoteFreightTemplate = templates.find(template => {
                    return (
                      template.language ===
                      this.quoteFreightTemplateTranslation.language
                    );
                  });

                  if (quoteFreightTemplate) {
                    this.updateQuoteFreightTemplateTranslation({
                      templateId: this.quoteFreightTemplate.id,
                      translation: this.quoteFreightTemplateTranslation
                    }).then((template: IQuoteFreightTemplateData) => {
                      this.quoteFreightTemplateTranslation = new QuoteFreightTemplate(
                        template
                      );
                      this.close();
                    });
                  } else {
                    this.createQuoteFreightTemplateTranslation({
                      templateId: this.quoteFreightTemplate.id,
                      translation: this.quoteFreightTemplateTranslation
                    }).then((template: IQuoteFreightTemplateData) => {
                      this.quoteFreightTemplateTranslation = new QuoteFreightTemplate(
                        template
                      );
                      this.close();
                    });
                  }
                });

                break;
              case QuoteTemplateTranslationLanguage.en:
              default:
                if (this.createForm) {
                  await this.createQuoteFreightTemplate(
                    this.quoteFreightTemplate
                  );
                } else {
                  await this.updateQuoteFreightTemplate(
                    this.quoteFreightTemplate
                  );
                }

                this.close();
            }
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              if (
                QuoteTemplateFieldError.isQuoteTemplateFieldError(
                  error.response.data
                )
              ) {
                this.fieldError = new QuoteTemplateFieldError(
                  error.response.data
                );
              } else if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }

            this.errorMessage = ErrorManager.extractApiError(error);
          } finally {
            this.loading = false;
          }
        }
      }
    }
  });
